import React, {useState} from "react";
import {DataGridPro} from '@mui/x-data-grid-pro';
import * as moment from "moment-timezone";
import {useMutation} from "@apollo/client";

import {TWENTY_FOUR_HOUR_TIME_FORMAT} from "../../config/DateTimeConstants";
import PerformerSelect from "./PerformerSelect";
import BookableStatusButton from "./BookableStatusButton";
import {
    buildCurrentEnrollments,
    buildEnrolledPerformerIds,
    isShiftPlanAfterCurrentTime,
    buildUnenrolledPerformerIds,
    isUnableToUnenrollUser,
    SHIFT_PLAN_ERROR_MESSAGE,
    SHIFT_PLAN_SUCCESS_MESSAGE
} from "../../helpers/ShiftPlanHelper";
import {SHIFT_SELECTION_STATUS} from "../../domain/ShiftPlanConstants";
import {
    DELETE_SHIFT,
    ENROLL_PERFORMER_FROM_SHIFT_PLANS,
    GET_DETAILED_SHIFT_PLANS,
    UNENROLL_PERFORMER_FROM_SHIFT_PLANS,
    UPDATE_PERFORMER_SHIFT_SELECTION
} from "../../queries/GraphQLQueries";
import CustomAlert from "../CustomAlert";
import DeleteShiftInlineDialog from "./DeleteShiftInlineDialog";

export default function ShiftPlanGrid({shiftPlans, setSelectedShiftPlanIds, selectedShiftPlanIds, setShiftBookableStatuses}) {
    const [rowBookableStatuses, setRowBookableStatuses] = useState({});

    const [alertMessage, setAlertMessage] = useState(null);
    const [openDeleteShiftDialog, setOpenDeleteShiftDialog] = useState(false);
    const isShiftPlanInPast = (shiftPlan) => moment(shiftPlan?.start).isBefore(moment());
    const areAllShiftPlansInPast = shiftPlans.every(shiftPlan => isShiftPlanInPast(shiftPlan));
    const hasNoShiftPlans = shiftPlans.length === 0;
    const [shiftToBeDeleted, setShiftToBeDeleted] = useState(null);
    const [performerName, setPerformerName] = useState(null);

    const columnHeaderCheckboxStyle = areAllShiftPlansInPast || hasNoShiftPlans ? {pointerEvents: "none", opacity: 0.5} : {};
    const dataGridStyle = {
        "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {outline: "none !important"},
        "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": columnHeaderCheckboxStyle,
        "& .MuiDataGrid-cell": {display: "flex", alignItems: "center"}
    };

    const refetchDetailedShiftPlans = {refetchQueries: [{query: GET_DETAILED_SHIFT_PLANS}, 'GetShiftPlansForTimeRange']};

    const [enrollPerformer] = useMutation(ENROLL_PERFORMER_FROM_SHIFT_PLANS, refetchDetailedShiftPlans);
    const [unenrollPerformer] = useMutation(UNENROLL_PERFORMER_FROM_SHIFT_PLANS, refetchDetailedShiftPlans);
    const [updatePerformerShiftSelection] = useMutation(UPDATE_PERFORMER_SHIFT_SELECTION, refetchDetailedShiftPlans);
    const [deleteShift] = useMutation(DELETE_SHIFT, refetchDetailedShiftPlans);

    const handleCloseCustomAlert = () => setAlertMessage(null);

    const handleBookableStatusChange = (shiftPlanId, updatedBookableStatuses) => {
        setShiftBookableStatuses(prevData => {
            const updatedData = { ...prevData };
            updatedData[shiftPlanId] = updatedBookableStatuses;
            return updatedData;
        });
    };

    const handlePerformerEnrollmentUpdate = (event, shiftPlanId) => {
        const selectedPerformerIds = event.target.value;
        const currentEnrollments = buildCurrentEnrollments(shiftPlans, shiftPlanId);

        if (isUnableToUnenrollUser(currentEnrollments, selectedPerformerIds)) {
            setAlertMessage({ children: SHIFT_PLAN_ERROR_MESSAGE.SHIFT_ALREADY_GENERATED, severity: 'error' });
            return;
        }

        const unenrolledPerformerIds = buildUnenrolledPerformerIds(currentEnrollments, selectedPerformerIds);

        if (unenrolledPerformerIds.length > 0) {
            unenrollPerformersFromShiftPlan(unenrolledPerformerIds, shiftPlanId);
            return;
        }

        const enrolledPerformerIds = buildEnrolledPerformerIds(currentEnrollments, selectedPerformerIds);
        enrollPerformersFromShiftPlan(enrolledPerformerIds, shiftPlanId);
    };

    const handlePerformerSelectionUpdated = (shiftPlanId, enrollment, performerId) => {

        const isShiftAlreadyGenerated = !!enrollment.availability;

        if (isShiftAlreadyGenerated) {
            handleShiftForDeletion(enrollment);
        } else {

            const isPerformerSelected = !(enrollment.shiftSelectionStatus === SHIFT_SELECTION_STATUS.SELECTED);

            updatePerformerShiftSelection({
                variables: {
                    shiftPlanIds: [shiftPlanId],
                    performerId,
                    isPerformerSelected
                }
            })
                .then(() => setAlertMessage({ children: SHIFT_PLAN_SUCCESS_MESSAGE.SELECTION_UPDATED, severity: 'success' }))
                .catch(error => {
                    setAlertMessage({ children: error?.message, severity: 'error' });
                    console.log("error " + error)
                });
        }
    }

    const handleShiftForDeletion = (enrollment) => {
        const hasAnyTeachingSessions = !!enrollment.availability.hasAnyTeachingSessions;

        if (hasAnyTeachingSessions) {
            setAlertMessage({ children: SHIFT_PLAN_ERROR_MESSAGE.SHIFT_ALREADY_HAS_SESSION, severity: 'error' });
        } else {
            setOpenDeleteShiftDialog(true);
            setPerformerName(enrollment.user.displayName);
            setShiftToBeDeleted(enrollment.availability.id);
        }
    };

    const handleShiftDelete = () => {
        setAlertMessage({ children: 'Deleting Shift...', severity: 'success' });

        deleteShift(
            {
                variables: {
                    shiftId: shiftToBeDeleted
                }
            }).then(() => {
                setAlertMessage({ children: 'Shift Deleted.', severity: 'success' });
            }).catch(error => {
                setAlertMessage({ children: error?.message, severity: 'error' });
                console.log(error);
            }).finally(() => setOpenDeleteShiftDialog(false));;
    }

    const unenrollPerformersFromShiftPlan = (unenrolledPerformerIds, shiftPlanId) => {
        unenrolledPerformerIds.forEach(performerId => {
            unenrollPerformer({
                variables: {
                    shiftPlanIds: [shiftPlanId],
                    performerId
                }
            })
            .then(() => setAlertMessage({children: SHIFT_PLAN_SUCCESS_MESSAGE.PERFORMER_UNENROLLED, severity: 'success'}))
            .catch(handleError);
        });
    };

    const enrollPerformersFromShiftPlan = (enrolledPerformerIds, shiftPlanId) => {
        enrolledPerformerIds.forEach(performerId => {
            enrollPerformer({
                variables: {
                    shiftPlanIds: [shiftPlanId],
                    performerId
                }
            })
            .then(() => setAlertMessage({children: SHIFT_PLAN_SUCCESS_MESSAGE.PERFORMER_ENROLLED, severity: 'success'}))
            .catch(handleError);
        });
    };

    const handleError = (error) => {
        setAlertMessage({ children: error?.message, severity: 'error' });
        console.log('error ' + error);
    };

    const columns = [
        {
            field: 'time',
            headerName: 'Time',
            flex: 1,
            maxWidth: 110,
            renderCell: (params) => `${moment(params.row.start).format(TWENTY_FOUR_HOUR_TIME_FORMAT)} - ${moment(params.row.end).format(TWENTY_FOUR_HOUR_TIME_FORMAT)}`
        },
        {
            field: 'availablePerformers',
            headerName: 'Available Performers',
            flex: 1,
            minWidth: 220,
            renderCell: (params) => <PerformerSelect
              shiftPlan={params.row}
              handlePerformerEnrollment={handlePerformerEnrollmentUpdate}
              handlePerformerSelection={handlePerformerSelectionUpdated} />
        },
        {
            field: 'bookableStatus',
            headerName: 'Bookable Services & Status',
            width: 200,
            renderCell: (params) => (
              <BookableStatusButton
                serviceTypePlans={params.row.bookableServiceTypePlans}
                bookableServices={params.row.bookableServices}
                bookableServiceTypes={rowBookableStatuses[params.row.id] || params.row.bookableServices.map(() => "Bookable")}
                setBookableServiceTypes={(updatedList) => {
                  setRowBookableStatuses(prev => ({...prev, [params.row.id] : updatedList}));
                  handleBookableStatusChange(params.row.id, updatedList);
                }}
              />
            )
        },
    ];

    return <>
        <DataGridPro
          checkboxSelection disableRowSelectionOnClick hideFooter
          sx={dataGridStyle}
          rows={shiftPlans}
          columns={columns}
          isRowSelectable={params => isShiftPlanAfterCurrentTime(params.row.start)}
          onRowSelectionModelChange={rows => setSelectedShiftPlanIds(rows)}
          rowSelectionModel={selectedShiftPlanIds}
          disableColumnMenu
          disableColumnFilter
          disableColumnSorting
          getRowHeight={() => 'auto'} />

        <DeleteShiftInlineDialog
            openDeleteShiftDialog={openDeleteShiftDialog}
            handleClose={() => setOpenDeleteShiftDialog(false)}
            handleShiftDelete={handleShiftDelete}
            performerName={performerName} />

        <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert}/>
    </>
};
